/**
 * Conditional social handle field
 *
 * The handle is asked for ONLY when the selected role is the solo trainer /
 * fitness creator card. It is optional even when visible: it never blocks
 * submission and never produces a validation error.
 *
 * Switching away from that role hides the field and clears the value that would
 * be submitted, but the typed text is kept in module state so switching back
 * restores it. A leading "@" is stripped server-side (`User#normalize_social_handle`)
 * — the client only echoes whatever the user typed.
 *
 * Markup contract (see app/views/users/shared/_registration_profile_fields.html.erb):
 *   [data-social-handle-field]  the wrapper toggled via the `hidden` attribute,
 *                               carrying data-social-handle-role with the role
 *                               id that reveals it
 *     [data-social-handle-input] the text input
 */

/** Fallback for markup that omits `data-social-handle-role`. */
const DEFAULT_REVEALING_ROLE = "solo-trainer";

export interface SocialHandleHandle {
  /**
   * React to a role change: reveal the field for the revealing role, hide and
   * clear it for anything else.
   */
  setRole: (roleId: string | null) => void;
  /** Tear down every listener this field registered. */
  destroy: () => void;
}

/**
 * Mount the conditional social handle field found inside a scope.
 *
 * @param scope - the element or document containing the field
 * @returns a handle, or `null` when the page renders no social handle field
 */
export function mountSocialHandle(
  scope: ParentNode = document,
): SocialHandleHandle | null {
  const field = scope.querySelector<HTMLElement>("[data-social-handle-field]");
  const input = scope.querySelector<HTMLInputElement>(
    "[data-social-handle-input]",
  );
  if (!field || !input) return null;

  const revealingRole =
    field.dataset.socialHandleRole || DEFAULT_REVEALING_ROLE;

  // Survives a hide/show round trip so a user who mis-clicks a role card does
  // not lose what they typed.
  let retained = input.value;

  const onInput = () => {
    retained = input.value;
  };
  input.addEventListener("input", onInput);

  return {
    setRole: (roleId: string | null) => {
      if (roleId === revealingRole) {
        field.hidden = false;
        input.value = retained;
        return;
      }
      retained = input.value || retained;
      input.value = "";
      field.hidden = true;
    },
    destroy: () => {
      input.removeEventListener("input", onInput);
    },
  };
}
